import {Box, Divider, Stack, Typography, useTheme, Button, FormControl, Select, InputLabel, MenuItem, Popover} from "@mui/material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import {useEffect, useState, useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import {ApiRequestFailed, authenticateAndFetchData, retryFn} from "@lib/apis";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {CustomCellRendererProps} from "ag-grid-react";
import {Link} from "react-router-dom";
import {urls} from "@routes";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import IconButton from "@mui/material/IconButton";
import { Downloading } from "@mui/icons-material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

interface User {
	id: number
	username: string
	email: string
	total_domains: number
	total_campaigns: number
	date_joined_ts: number
	plan_name: string
	connected_domain_names: string[]
	verified_email: boolean
	early_access: boolean
	signup_plan_selection_done: boolean
	has_purchased_plan: boolean
	spam_ban: boolean
}

interface PaginationData {
	data: User[];
	total_count: number;
	total_pages: number;
	current_page: number;
	page_size: number;
}

export default function AdminUsers() {
	const theme = useTheme();
	const { user } = useAuth();
	const { enqueueSnackbar } = useSnackbar();
	const BOOLEAN_FILTER_FIELDS = [
		{ label: "Verified Email", value: "verified_email" },
		{ label: "Early Access", value: "early_access" },
		{ label: "Signup Plan Selected", value: "signup_plan_selection_done" },
		{ label: "Purchased Plan", value: "has_purchased_plan" },
	];
	
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");
	const [domainSearch, setDomainSearch] = useState("");
	const [pageData, setPageData] = useState<PaginationData>();
	const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
	const openFilter = (e: React.MouseEvent<HTMLElement>) => setFilterAnchor(e.currentTarget);
	const closeFilter = () => setFilterAnchor(null);
	const isFilterOpen = Boolean(filterAnchor);	
	const [appliedFilterField, setAppliedFilterField] = useState("");
	const [appliedFilterValue, setAppliedFilterValue] = useState("");
	const [draftFilterField, setDraftFilterField] = useState("");
	const [draftFilterValue, setDraftFilterValue] = useState("");
	const [exportLoading, setExportLoading] = useState(false);


	// Query to fetch all users.
	const fetchAllUsersQuery = useQuery({
		queryKey: [
			"fetchAllUsersQuery",
			page,
			pageSize,
			search,
			domainSearch,
			appliedFilterField,
			appliedFilterValue
		],
		queryFn: () =>
			authenticateAndFetchData(
				`/admin/all-users/?page=${page}&page_size=${pageSize}&search=${search}&domain_search=${domainSearch}&filter_field=${appliedFilterField}&filter_value=${appliedFilterValue}`
			),
		gcTime: 0,
		refetchOnWindowFocus: false,
		retry: retryFn,
	});

	useEffect(() => {
		if (fetchAllUsersQuery.data?.data) {
			const response = fetchAllUsersQuery.data.data;

			setPageData({
				data: response.users || [],
				total_count: response.total_count,
				total_pages: response.total_pages,
				current_page: response.current_page,
				page_size: response.page_size,
			});
		}
	}, [fetchAllUsersQuery.data]);

	// const filteredUsers = useMemo(() => {
	// 	if (!pageData?.users) return [];

	// 	let users = [...pageData.users];

	// 	if (appliedFilterField && appliedFilterValue !== "") {
	// 		users = users.filter(
	// 			(user) =>
	// 				String(user[appliedFilterField as keyof User]) === appliedFilterValue
	// 		);
	// 	}

	// 	return users;
	// }, [pageData?.users, appliedFilterField, appliedFilterValue]);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	const handleDomainSearchChange = (value: string) => {
		setDomainSearch(value);
		setPage(1);
	};	

	function timestampFormatter(params: ValueFormatterParams) {
		if (params.value === null) {
			return "---"
		}
		return format(new Date(params.value), "do MMM y, h:mm a");
	}


	const downloadUserCSV = (data: User[]) => {
		if (!data || data.length === 0) {
			enqueueSnackbar("No data to export.", { variant: "error" });
			return;
		}

		const escapeCSV = (value: any) => {
			if (value == null) return "";
			const stringValue = String(value);
			return `"${stringValue.replace(/"/g, '""')}"`;
		};

		// Step 1: Define headers
		const csvHeader = ["Name", "Email", "Plan", "Verified email", "Signup date-time"];

		// Step 2: Build data rows
		const csvRows = data.map((user) => [
			escapeCSV(user.username),
			escapeCSV(user.email),
			escapeCSV(user.plan_name),
			escapeCSV(user.verified_email ? "True" : "False"),
			escapeCSV(format(new Date(user.date_joined_ts), "yyyy-MM-dd HH:mm:ss")),
		]);

		// Step 3: Combine header + rows
		const csvContent = [csvHeader, ...csvRows].map((row) => row.join(",")).join("\n");

		// Step 4: Trigger CSV download
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = getExportFilename(user?.email ?? "unknown", "User");
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleExportClick = async () => {
		setExportLoading(true);

		try {
			const response = await authenticateAndFetchData(
				"/admin/all-users/export/"
			);

			const users = response?.data?.users;

			if (!users || users.length === 0) {
				enqueueSnackbar("No data available for export.", {
					variant: "error",
				});
				return;
			}

			downloadUserCSV(users);

			enqueueSnackbar("CSV downloaded successfully!", {
				variant: "success",
			});

		} catch (err) {
			console.error(err);

			enqueueSnackbar("Error while exporting users.", {
				variant: "error",
			});
		} finally {
			setExportLoading(false);
		}
	};

	const ExportUserCsvButton = () => {
		return (
			<Button startIcon={<Downloading />}
				variant="contained"
				color="primary"
				onClick={() => {
					handleExportClick();
				}}
				disabled={exportLoading}
			>
				{exportLoading ? "Exporting..." : "Export"}
			</Button>
		)
	}

	const TableActions = () => (
		<Stack direction="row" spacing={1}>
			<IconButton onClick={openFilter}>
				<FilterListIcon />
			</IconButton>
			<ExportUserCsvButton />
		</Stack>
	);

	const columnCustomizerLabel = {
		email: "Email ID",
		username: "Username",
		id: "DB ID",
		date_joined_ts: "Created On",
		total_domains: "Total Domains",
		total_campaigns: "Total Campaigns",
		last_activity: "Last Activity",
		plan_name: "Plan Name",			
	};

	// Columns for user table.
	const columnDefs: ColDef[] = [
		{field: "email", headerName: "Email ID", cellRenderer: UserDetailsLink},
		{field: "username", headerName: "Username"},
		{field: "id", headerName: "DB ID"},
		{field: "date_joined_ts", headerName: "Created On", valueFormatter: timestampFormatter},
		{field: "total_domains", headerName: "Total Domains"},
		{field: "total_campaigns", headerName: "Total Campaigns", resizable: false},
		{ field: "last_activity", headerName: "Last Activity", resizable: false, valueFormatter: timestampFormatter },
		{field: "plan_name", headerName: "Plan Name"},		
		{field: "spam_ban", headerName: "Spam Ban", width: 120, valueGetter: (params) => params.data.spam_ban ? "Banned" : "Active" },
	]

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (!pageData && fetchAllUsersQuery.isLoading) {
		return (
			<PageLoading/>
		)

	} else if (fetchAllUsersQuery.error as unknown as ApiRequestFailed) {
		return <PageDataErrorHandler error={fetchAllUsersQuery.error as unknown as ApiRequestFailed}/>

	} else if (pageData) {
		return (
			<Stack direction={"column"} spacing={2} sx={{pb: 4, height: "100%"}}>
				<Typography fontWeight={"bold"} variant={"h5"}>Manage All User Accounts</Typography>
				<Divider/>
				<Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
					<PaginatedServerSideDataTable
						columns={columnDefs}
						paginationData={pageData}
						loading={fetchAllUsersQuery.isLoading || fetchAllUsersQuery.isFetching}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
						onSearchChange={handleSearchChange}
						onDomainSearchChange={handleDomainSearchChange}
						showDomainSearch={true}
						searchPlaceholder="Search by email or username..."
						domainSearchPlaceholder="Search by domain..."
						actions={TableActions()}
						columnLabels={columnCustomizerLabel}
					/>
				</Box>
				<Popover
					open={isFilterOpen}
					anchorEl={filterAnchor}
					onClose={closeFilter}
					anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
					transformOrigin={{ vertical: "top", horizontal: "left" }}
					PaperProps={{ sx: { p: 2, width: 260 } }}
				>
					<Stack spacing={2}>
						{/* Select Field */}
						<FormControl fullWidth size="small">
							<InputLabel>Filter Field</InputLabel>
							<Select
								value={draftFilterField}
								label="Filter Field"
								onChange={(e) => {
									setDraftFilterField(e.target.value);
									setDraftFilterValue(""); // reset value on field change
								}}
							>
								<MenuItem value="">None</MenuItem>

								{BOOLEAN_FILTER_FIELDS.map((field) => (
									<MenuItem key={field.value} value={field.value}>
										{field.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						{/* Select Value */}
						{draftFilterField && (
							<FormControl fullWidth size="small">
								<InputLabel>Value</InputLabel>
								<Select
									value={draftFilterValue}
									label="Value"
									onChange={(e) => setDraftFilterValue(e.target.value)}
								>
									<MenuItem value="">All</MenuItem>
									<MenuItem value="true">Yes</MenuItem>
									<MenuItem value="false">No</MenuItem>
								</Select>
							</FormControl>
						)}

						{/* Buttons */}
						<Stack direction="row" justifyContent="space-between">
							<Button
								variant="outlined"
								onClick={() => {
									setDraftFilterField("");
									setDraftFilterValue("");
									setAppliedFilterField("");
									setAppliedFilterValue("");
									closeFilter();
								}}
							>
								Clear
							</Button>

							<Button
								variant="contained"
								disabled={!draftFilterField}
								onClick={() => {
									setAppliedFilterField(draftFilterField);
									setAppliedFilterValue(draftFilterValue);
									setPage(1);
									closeFilter();
								}}
							>
								Apply
							</Button>
						</Stack>
					</Stack>
				</Popover>
			</Stack>
		)

	} else {
		// Ideally it should not reach here.
		return <></>
	}
}

export const UserDetailsLink = (cellProps: CustomCellRendererProps) => {
	const theme = useTheme();
	
	const handleCopy = (e: React.MouseEvent) => {
		e.preventDefault(); // Prevents link navigation
		e.stopPropagation(); // Prevents grid row selection
		if (cellProps.value) {
			navigator.clipboard.writeText(cellProps.value);
			// toast.success("Copied!");
		}
	};

	return (
		<div 
			className="copy-container" 
			style={{ 
				display: 'flex', 
				alignItems: 'center', 
				justifyContent: 'space-between', 
				width: '100%',
				height: '100%'
			}}
		>
			<Link 
				to={urls["adminUserDetails"].replace(":userId", cellProps.data["id"])}
				style={{
					color: theme.palette.text.primary,
					overflow: 'hidden', 
					textOverflow: 'ellipsis', 
					whiteSpace: 'nowrap'
				}}
			>
				{cellProps.value}
			</Link>

			<button
				onClick={handleCopy}
				className="copy-button"
				title="Copy to clipboard"
				style={{
					border: 'none',
					background: 'transparent',
					cursor: 'pointer',
					padding: '4px',
					display: 'flex',
					alignItems: 'center',
					color: '#666',
					transition: 'opacity 0.2s ease-in-out'
				}}
			>
				<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
					<path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
				</svg>
			</button>

			{/* Hover CSS */}
			<style>{`
				.copy-button { opacity: 0; }
				.copy-container:hover .copy-button { opacity: 1; }
			`}</style>
		</div>
	);
};